<template>
    <div class="airport-search-input">
        <input type="hidden" ref="hiddenInput" :name="name" :value="value" />
        <md-field class="with-left-icon" v-bind:class="{'md-invalid': !!inputError}">
            <label v-if="!noLabel">{{ label }}</label>
            <!-- Do not remove. This label is required to disable Chrome's autofill feature -->
            <label style="display: none;">disable autofill</label>
            <custom-icon v-if="!noIcon" class="md-prefix" :name="useIcon" />
            <md-input
                type="text"
                v-model="selectedAirportName"
                ref="searchInput"
                :placeholder="label"
                v-on:blur="onBlur($event)"
                :disabled="disabled"
                dusk="airport-search-input"
                :autofocus="autofocus"
                id="airport-search-input"
            />
            <md-progress-spinner
                v-show="searching"
                md-mode="indeterminate"
                :md-diameter="20"
                :md-stroke="3"
            />
            <span class="md-error">{{ inputError ? inputError[0] : '' }}</span>
        </md-field>
    </div>
</template>

<script>
export default {
    props: {
        name: String,
        label: String,
        value: Number,
        airportName: String,
        fromAirportName: String,
        fromAirportOaci: String,
        icon: String,
        noLabel: Boolean,
        inputError: Array,
        isSeoDestinationSelect: {
            type: Boolean,
            default: false,
        },
        disabled: String,
        autofocus: String,
        nameField: {
            type: String,
            default: 'full_name',
        },
        noIcon: {
            type: Boolean,
            default: false,
        },
        searchNearCities: {
            type: Boolean,
            default: true,
        },
    },
    data: () => {
        return {
            searchTimeout: null,
            searchNearCity: null,
            source: null,
            typeahead: null,
            storedAirportName: null,
            overrideAirportName: null,
            searching: false,
            limit: 10,
            touched: false,
        };
    },
    computed: {
        selectedAirportName: {
            get() {
                if (this.airportName && this.overrideAirportName === null) {
                    this.overrideAirportName = this.airportName;
                }
                return this.overrideAirportName !== null
                    ? this.overrideAirportName
                    : this.airportName;
            },
            set(value) {
                this.overrideAirportName = value;
            },
        },
        useIcon() {
            return this.icon ? this.icon : "flight_takeoff";
        },
    },
    mounted() {
        this.storedAirportName = this.airportName;
        this.initSource();
        this.initTypeahead();
    },
    methods: {
        initSource() {
            this.source = (query, syncResults, asyncResults) => {
                if (this.touched) {
                    if (this.searchTimeout) {
                        clearTimeout(this.searchTimeout);
                    }
                    this.searching = true;
                    if (this.searchNearCity) {
                        $.get(
                            "/api/airports",
                            {
                                near_city: this.searchNearCity["id"],
                                per_page: this.limit,
                                disable_pagination: 1,
                            },
                            (airportResults) => {
                                asyncResults(
                                    airportResults.map((a) => {
                                        a["type"] = "airport";
                                        a["icon"] = "flight";
                                        a["label"] = a[this.nameField];
                                        a["distance"] = a["distance"].toFixed(
                                            0
                                        );
                                        return a;
                                    })
                                );
                                this.searching = false;
                            }
                        );
                        this.searchNearCity = null;
                    } else {
                        this.searchTimeout = setTimeout(() => {
                            this.searchTimeout = null;
                            $.get(
                                "/api/airports",
                                {
                                    term: query,
                                    per_page: this.limit,
                                    disable_pagination: 1,
                                    with_seo_flight_url_from: this.fromAirportOaci,
                                },
                                (airportResults) => {
                                    let finalResults = airportResults.map(
                                        (a) => {
                                            a["type"] = "airport";
                                            a["icon"] = "flight";
                                            a["label"] = a[this.nameField];
                                            return a;
                                        }
                                    );
                                    if (this.searchNearCities && finalResults.length < this.limit) {
                                        $.get(
                                            "/api/cities",
                                            {
                                                term: query,
                                                per_page:
                                                    this.limit -
                                                    finalResults.length,
                                                disable_pagination: 1,
                                                with_country: 1,
                                            },
                                            (cityResults) => {
                                                finalResults = finalResults.concat(
                                                    cityResults.map((c) => {
                                                        c["type"] = "city";
                                                        c["icon"] =
                                                            "location_city";
                                                        c[
                                                            "label"
                                                        ] = Vue.i18n.translate(
                                                            "registration.search-near-city",
                                                            {
                                                                city: c[
                                                                    "name_list"
                                                                ].split(",")[0],
                                                                department_country:
                                                                    (c[
                                                                        "adminLevel2"
                                                                    ]
                                                                        ? c[
                                                                              "adminLevel2"
                                                                          ][
                                                                              "name"
                                                                          ] +
                                                                          ", "
                                                                        : c[
                                                                              "adminLevel1"
                                                                          ]
                                                                        ? c[
                                                                              "adminLevel1"
                                                                          ][
                                                                              "name"
                                                                          ] +
                                                                          ", "
                                                                        : c[
                                                                              "postalCode"
                                                                          ]
                                                                        ? c[
                                                                              "postalCode"
                                                                          ] +
                                                                          ", "
                                                                        : "") +
                                                                    c[
                                                                        "country"
                                                                    ]["nom"],
                                                            }
                                                        );
                                                        return c;
                                                    })
                                                );
                                                asyncResults(finalResults);
                                                this.searching = false;
                                            }
                                        );
                                    } else {
                                        asyncResults(finalResults);
                                        this.searching = false;
                                    }
                                }
                            );
                        }, 300);
                    }
                }
            };
        },
        initTypeahead() {
            this.typeahead = $(this.$refs.searchInput.$el).typeahead(null, {
                display: this.nameField,
                source: this.source,
                templates: {
                    suggestion: Handlebars.compile(
                        '<div><i class="material-icons">{{icon}}</i> {{label}}\
                            {{#if distance}}\
                                <small>({{distance}} km)</small>\
                            {{/if}}\
                        </div>'
                    ),
                },
                hint: false,
                minLength: 2,
                limit: 2 * this.limit,
            });
            this.typeahead.bind("typeahead:select", (event, suggestion) => {
                if (suggestion["type"] == "city") {
                    this.$emit("input", null);
                    setTimeout(() => {
                        this.searchNearCity = suggestion;
                        // Do not remove unbreakable space here, it's a workaround for a weird bug on touch devices
                        $(this.$refs.searchInput.$el).typeahead(
                            "val",
                            " " + suggestion["name_list"].split(",")[0]
                        );
                    }, 100);
                } else {
                    this.$emit("input", suggestion["idAeroport"]);
                    this.selectedAirportName = suggestion[this.nameField];
                    this.storedAirportName = suggestion[this.nameField];
                    if (this.isSeoDestinationSelect) {
                        this.redirectToSeoFlightPage(suggestion);
                    }
                }
            });
        },
        onBlur(event) {
            if (!this.value || this.overrideAirportName === "") {
                this.$emit("input", null);
                this.storedAirportName = "";
                this.overrideAirportName = "";
            } else if (this.overrideAirportName !== this.storedAirportName) {
                this.overrideAirportName = this.storedAirportName;
                this.typeahead.typeahead("val", "");
            }
            this.touched = false;
        },
        clearAndFocus() {
            this.selectedAirportName = "";
            this.$refs.searchInput.$el.focus();
        },
        redirectToSeoFlightPage(destAirport) {
            window.location = destAirport.seo_flight_url;
        },
    },
    watch: {
        selectedAirportName(value) {
            this.touched = true;
        },
    },
};
</script>
<style lang="scss" >
@import "~styles/_variables";
#airport-search-input {
    color: $primary-color-dark;
}
#airport-search-input::placeholder {
    color: $primary-color-dark;
    font-size: 16px;
    line-height: 20px;
}
.airport-search-input {
    .tt-menu {
        z-index: 1000 !important;
    }
    .twitter-typeahead, input {
        width: 100%;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/widgets/AirportInputComponent.vue